import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';

interface UseGoBackOptions {
  fallbackPath?: string;
  checkHistory?: boolean;
}

interface UseGoBackReturn {
  goBack: () => void;
  canGoBack: boolean;
}

/**
 * Hook personalizado para manejar la navegación hacia atrás de forma robusta
 * Proporciona una función `goBack` que intenta navegar hacia atrás en el historial,
 * pero con un fallback seguro si no hay historial disponible.
 * 
 * @param options - Opciones de configuración
 * @param options.fallbackPath - Ruta de fallback (por defecto '/')
 * @param options.checkHistory - Si verificar el historial del navegador (por defecto true)
 * @returns Objeto con función goBack y estado canGoBack
 */
export const useGoBack = (options: UseGoBackOptions = {}): UseGoBackReturn => {
  const {
    fallbackPath = '/',
    checkHistory = true
  } = options;

  const navigate = useNavigate();
  const location = useLocation();

  // Verificar si hay historial disponible
  const canGoBack = checkHistory ? window.history.length > 1 : true;

  const goBack = useCallback(() => {
    // Si hay un estado 'from' en la navegación, usarlo como preferencia
    const fromPath = location.state?.from;

    if (fromPath && fromPath !== location.pathname) {
      navigate(fromPath);
      return;
    }

    // Si hay historial disponible, ir hacia atrás
    if (canGoBack) {
      navigate(-1);
      return;
    }

    // Fallback: navegar a la ruta especificada
    navigate(fallbackPath);
  }, [navigate, location, canGoBack, fallbackPath]);

  return {
    goBack,
    canGoBack
  };
};

/**
 * Hook simplificado que solo proporciona la función goBack
 * Ideal para casos donde no necesitas verificar el estado
 * 
 * @param fallbackPath - Ruta de fallback (por defecto '/')
 * @returns Función goBack
 */
export const useGoBackSimple = (fallbackPath: string = '/') => {
  const { goBack } = useGoBack({ fallbackPath });
  return goBack;
};

/**
 * Función utilitaria para navegar con información del origen
 * Útil para rastrear de dónde viene el usuario
 * 
 * @param navigate - Función navigate de React Router
 * @param to - Ruta de destino
 * @param currentPath - Ruta actual (opcional)
 * @param state - Estado adicional (opcional)
 */
export const navigateWithFrom = (
  navigate: ReturnType<typeof useNavigate>,
  to: string,
  currentPath?: string,
  state?: any
) => {
  navigate(to, {
    state: {
      ...state,
      from: currentPath || window.location.pathname
    }
  });
};


/**
 * Hook para manejar la navegación hacia el home
 * Proporciona una función `goHome` que navega a la ruta raíz ('/')
 * @returns Objeto con función goHome
 */
export const useGoHome = () => {
  const navigate = useNavigate();

  const goHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  return {
    goHome
  };
};

/**
 * Hook para manejar la navegación hacia el dashboard
 * Proporciona una función `goDashboard` que navega a la ruta '/dashboard'
 * @returns Objeto con función goDashboard
 */
export const useGoDashboard = () => {
  const navigate = useNavigate();

  const goDashboard = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  return {
    goDashboard
  };
};
